import React, { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { generateUniqueNames } from "../utils.tsx";
import { Parameter } from '../scanTypes.ts';
import { BeamlineContext } from '../beamlineProvider.tsx';
import { ParameterCreateDto } from '../api/api.ts';
import { SelectingStatus } from '../components/inputAutocomplete.tsx'
import { ParameterAutocomplete, ParameterSearchFunctions } from '../components/parameterAutocomplete.tsx';


const AddParameter: React.FC = () => {

  const beamlineContext = useContext(BeamlineContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [newName, setNewName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [derivedParameterValue, setDerivedParameterTypeValue] = useState<Parameter | null>(null)

  const [validName, setValidName] = useState<boolean>(true);
  const [uniqueName, setUniqueName] = useState<boolean>(true);
  const [validAllInput, setValidAllInput] = useState<boolean>(true);

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);


  function clickedOpen() {
    if (!inProgress && !isOpen) {
      setIsOpen(true);
      let existingNames: string[] = beamlineContext.scanTypes.parameterIdsInDisplayOrder.map((id) => beamlineContext.scanTypes.parametersById.get(id)!.name);

      const goodNames = generateUniqueNames(existingNames, "New Parameter", 1);
      setNewName(goodNames[0]);
      validate(goodNames[0]);
    }
  }


  function changedName(c:React.ChangeEvent<HTMLInputElement>) {
    const v = c.target.value;
    setNewName(v);
    validate(v);
  }


  function changedDescription(c:React.ChangeEvent<HTMLInputElement>) {
    const v = c.target.value;
    setDescription(v);
  }


  function validate(name:string) {
    var validName:boolean = true;
    var uniqueName:boolean = true;

    const trimmed = name.toString().trim();
    if (trimmed.length < 1) {
      validName = false;
    } else {
      let existingNames: string[] = beamlineContext.scanTypes.parameterIdsInDisplayOrder.map((id) => beamlineContext.scanTypes.parametersById.get(id)!.name);
      if (existingNames.includes(trimmed)) {
        validName = false;
        uniqueName = false;
      }
    }

    setValidName(validName);
    setUniqueName(uniqueName);
    setValidAllInput(validName);
  }


  async function pressedSubmit() {
    setSubmitErrorMessage(null);
    setInProgress(true);

    const dto: ParameterCreateDto = {
      name: newName,
      description: description,
      slug_beamline: beamlineContext.beamline!.slug,
    }
    if (derivedParameterValue) {
      dto.id_derived_from = derivedParameterValue.id;
      dto.standard_default = derivedParameterValue.standardDefault;
      dto.enforce_unique_in_set = derivedParameterValue.enforceUniqueInSet;
      dto.auto_generate_interval = derivedParameterValue.autoGenerateInterval;
      if (derivedParameterValue.choices) {
        dto.choices = derivedParameterValue.choices;
      }
      if (derivedParameterValue.validatorObject) {
        dto.validation = derivedParameterValue.validatorObject;
      }
      dto.schema_identifier = derivedParameterValue.schemaIdentifier;
    }

    const result = await beamlineContext.scanTypes.createParameter(dto);
    setInProgress(false);
    if (result.success) {
      beamlineContext.changed();
      setIsOpen(false);
    } else {
      setSubmitErrorMessage(result.message || "Error creating parameter.");
    }
  }
  

  function clickedClose() {
    if (!inProgress && isOpen) { setSubmitErrorMessage(null); setIsOpen(false); }
  }


  const parameterSearchFunctions:ParameterSearchFunctions = {
    itemSelected: (item: Parameter) => { setDerivedParameterTypeValue(item); validate(newName); return SelectingStatus.Success },
    selectedNone: () => { setDerivedParameterTypeValue(null); validate(newName); return SelectingStatus.Success }
  };


  return (
    <div>

      <button className="button" onClick={ clickedOpen }>Add Parameter</button>

      <div id="modal-add-parameter" className={ isOpen ? "modal is-active" : "modal" }>
        <div className="modal-background"></div>

        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Add Parameter</p>
            <button className="delete" aria-label="close" onClick={ clickedClose }></button>

          </header>
          <section className="modal-card-body">

            <div className="field">
              <label className="label">Derived From</label>
                <ParameterAutocomplete
                    selectedItem={derivedParameterValue}
                    searchFunctions={parameterSearchFunctions}
                  />
            </div>

            <p>When you select a "Derived From" Parameter, the new Parameter will be created with the same settings assigned to it.</p>

            <div className="field">
                <label className="label">Name</label>
              <div className="control has-icons-right">
                <input className={ validName ? "input" : "input is-danger" }
                  type="text"
                  placeholder="Unique name"
                  value={ newName }
                  onChange={ changedName } />
                <span className="icon is-small is-right">
                  { validName || (<FontAwesomeIcon icon={faExclamationTriangle} color="darkred" />) }
                </span>
              </div>
              { validName || (uniqueName ?
                              (<p className="help is-danger">This name is invalid</p>) :
                              (<p className="help is-danger">This name is not unique</p>))
              }
            </div>

            <div className="field">
              <label className="label">Description</label>
              <div className="control">
                <input className="input"
                  type="text"
                  placeholder="Optional description"
                  value={ description }
                  onChange={ changedDescription } />
              </div>
            </div>

            <div className="buttons">
              <input
                type="button"
                className="button is-success"
                onClick={ pressedSubmit }
                disabled={ inProgress || !validAllInput }
                value="Add"
              />
              <button className="button" onClick={ clickedClose }>Cancel</button>
            </div>

            { submitErrorMessage && (
              <article className="message is-danger">
                <div className="message-body">
                  { submitErrorMessage }
                </div>
              </article>
            )}

          </section>

        </div>
      </div>

    </div>
  )
}
export default AddParameter
